import { AsyncPipe, CurrencyPipe } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe } from '@jsverse/transloco';
import { InitialDepositPipe } from '@zero/onboarding/domain';
import { InputComponent } from '@zero/shared/ui-web-design-system';
import { BehaviorSubject, combineLatest, filter, map } from 'rxjs';
import { BaseComponent } from '../../components/base.component';
import { OnboardingContainerComponent } from '../../components/onboarding-container/onboarding-container.component';

@Component({
  imports: [OnboardingContainerComponent, TranslocoPipe, InputComponent, AsyncPipe, InitialDepositPipe, CurrencyPipe],
  templateUrl: './initial-deposit.component.html',
})
export class InitialDepositComponent extends BaseComponent {
  readonly #doMoneyTransferLater = new BehaviorSubject<boolean>(false);
  readonly #value = new BehaviorSubject<string>('');
  readonly #destroyRef = inject(DestroyRef);

  public readonly doMoneyTransferLater$ = this.#doMoneyTransferLater.asObservable();
  public readonly value$ = this.#value.asObservable();
  public readonly isFormValid$ = combineLatest([this.value$, this.doMoneyTransferLater$, this.facade.sendingInitialDeposit$]).pipe(
    map(([value, doMoneyTransferLater, sendingInitialDeposit]) =>
      doMoneyTransferLater
        ? !sendingInitialDeposit
        : value && !sendingInitialDeposit && new InitialDepositPipe().transform(value, this.maxValue) === null,
    ),
  );
  public maxValue: number;

  protected override submit(): Promise<void> | void {
    this.#doMoneyTransferLater.value ? this.facade.sendInitialDeposit(0) : this.facade.sendInitialDeposit(Number(this.#value.value));
  }

  protected override populateFromData(): void {
    this.#listenToDirectDebitPossibility();
    this.facade.onboardingData$
      .pipe(
        filter((data) => !!data),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(({ directDebitAmount, directDebitAmountLimitMax }) => {
        this.#value.next(directDebitAmount?.toString() || '');
        this.maxValue = directDebitAmountLimitMax;
      });
  }

  protected override listenToSuccessfulSubmit(): void {
    this.facade.sentInitialDepositSuccessfully$
      .pipe(
        filter((sent) => !!sent),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.facade.goToNextStep());
  }

  public goBack() {
    if (this.#doMoneyTransferLater.value) {
      this.#doMoneyTransferLater.next(false);
      return;
    }
    this.facade.goToPreviousStep();
  }

  public setMoneyTransferLater = () => {
    this.#value.next('');
    this.#doMoneyTransferLater.next(true);
    this.facade.doInitialDepositLater();
  };

  public onValueChange = (value: string) => this.#value.next(value);

  #listenToDirectDebitPossibility() {
    this.facade.onboardingData$
      .pipe(
        filter((data) => !!data),
        filter((data) => !data?.directDebitPossible),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.facade.goToNextStep());
  }
}
